import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import AdvancedFilters from '../../components/common/AdvancedFilters';
import ViewTicketModal from '../../components/features/tickets/ViewTicketModal';
import AddTicketModal from '../../components/features/tickets/AddTicketModal';
import EditTicketModal from '../../components/features/tickets/EditTicketModal';
import ApproveTicketModal from '../../components/features/tickets/ApproveTicketModal';
import ReopenTicketModal from '../../components/features/tickets/ReopenTicketModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useSite } from '../../contexts/SiteContext';
import customerService from '../../services/customerService';
import ticketService from '../../services/ticketService';
import { formatDateTime } from '../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS, TICKET_STATUSES } from '../../constants/statuses';
import SignatureCanvas from 'react-signature-canvas';

const TicketsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedSite, sites } = useSite();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const status = searchParams.get('status');
    const priority = searchParams.get('priority');
    if (status) return { status };
    if (priority) return { priority };
    return {};
  });

  // Contract verification states
  const [canCreateTicket, setCanCreateTicket] = useState(false);
  const [contractWarning, setContractWarning] = useState('');

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [approveTicket, setApproveTicket] = useState(null);
  const [reopenTicket, setReopenTicket] = useState(null);
  
  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    if (selectedSite !== null) {
      loadData();
    }
  }, [selectedSite]);

  useEffect(() => {
    filterTickets();
  }, [searchTerm, advancedFilters, tickets]);

  const loadData = async () => {
    setLoading(true);
    
    const [ticketsResult, dashboardResult] = await Promise.all([
      ticketService.getAllTickets(),
      customerService.getDashboardData(user.id, selectedSite)
    ]);

    if (dashboardResult.success) {
      const amcStatus = dashboardResult.data.stats.amcStatus.status;
      // Define what statuses allow ticket creation
      const isContractActive = ['Active', 'Valid', 'Expiring Soon'].includes(amcStatus);
      setCanCreateTicket(isContractActive);
      if (!isContractActive) {
        setContractWarning('Ticket creation is disabled because there is no active contract for this site.');
      } else {
        setContractWarning('');
      }
    }

    if (ticketsResult.success) {
      const customerTickets = ticketsResult.data.filter(t => 
        // (t.customer_id === user.id || t.customer_id === parseInt(user.id)) &&
        (selectedSite === 0 || t.site_id === selectedSite || t.site_id === String(selectedSite))
      );
      
      setTickets(customerTickets);
      
      setStats({
        total: customerTickets.length,
        open: customerTickets.filter(t => [
          TICKET_STATUSES.PENDING_ASSIGNMENT,
          TICKET_STATUSES.ASSIGNED,
          TICKET_STATUSES.WORK_IN_PROGRESS,
          TICKET_STATUSES.PENDING,
          TICKET_STATUSES.REASSIGNED,
          TICKET_STATUSES.REOPENED,
        ].includes(t.status)).length,
        pending: customerTickets.filter(t => [TICKET_STATUSES.PENDING].includes(t.status)).length,
        critical: customerTickets.filter(t => t.priority === 'CRITICAL').length
      });
    }
    setLoading(false);
  };

  const filterTickets = () => {
    let filtered = [...tickets];

    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id.toString().includes(searchTerm) ||
        t.system_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (advancedFilters.status) {
      if(advancedFilters.status === 'OPEN'){
        filtered = filtered.filter(t => [
          'PENDING','ASSIGNED','WORK_IN_PROGRESS','PENDING_ASSIGNMENT','REASSIGNED','REOPENED'
        ].includes(t.status));
      }else if(advancedFilters.status === 'CLOSED'){
        filtered = filtered.filter(t => t.status === 'CLOSED');
      }else {
      filtered = filtered.filter(t => t.status === advancedFilters.status);
      }
    }

    if (advancedFilters.priority) {
      filtered = filtered.filter(t => t.priority === advancedFilters.priority);
    }
    if (advancedFilters.site) {
      filtered = filtered.filter(t => t.site_id?.toString() === advancedFilters.site);
    }
    if (advancedFilters.startDate) {
      filtered = filtered.filter(t => new Date(t.created_at) >= new Date(advancedFilters.startDate));
    }
    if (advancedFilters.endDate) {
      const endDate = new Date(advancedFilters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(t => new Date(t.created_at) <= endDate);
    }

    setFilteredTickets(filtered);
  };

  const handleTicketCardClick = (filter) => {
    setSearchTerm('');

    if (filter === 'ALL') {
      setAdvancedFilters({});
      setSearchParams({});
    } else if (filter === 'OPEN') {
      setAdvancedFilters({ status: 'OPEN' });
      setSearchParams({ status: 'OPEN' });
    } else if (filter === 'PENDING') {
      setAdvancedFilters({ status: 'PENDING' });
      setSearchParams({ status: 'PENDING' });
    } else if (filter === 'CRITICAL') {
      setAdvancedFilters({ priority: 'CRITICAL' });
      setSearchParams({ priority: 'CRITICAL' });
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowViewModal(true);
  };

  const handleAddTicket = async (ticketData, attachments = []) => {
    const ticketWithCustomerId = {
      ...ticketData,
      customer_id: user.id
    };
    
    const result = await ticketService.createTicket(ticketWithCustomerId);
    
    if (result.success) {
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          await ticketService.uploadAttachment(result.data.id, attachment.file);
        }
        success('Ticket created with attachments!');
      } else {
        success('Ticket created successfully!');
      }
      
      await loadData();
    } else {
      error(result.error || 'Failed to create ticket');
    }
  };

  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
  };

  const handleUpdateTicket = async (ticketId, ticketData, attachments = []) => {
    const result = await ticketService.updateTicket(ticketId, ticketData);
    if (result.success) {
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          await ticketService.uploadAttachment(ticketId, attachment.file);
        }
        success('Ticket updated with new attachments!');
      } else {
        success('Ticket updated successfully!');
      }
      await loadData();
    } else {
      error(result.error || 'Failed to update ticket');
    }
  };

  const handleDeleteTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await ticketService.deleteTicket(selectedTicket.id);
    if (result.success) {
      success('Ticket deleted successfully!');
      await loadData();
    } else {
      error(result.error || 'Failed to delete ticket');
    }
    setShowDeleteDialog(false);
    setSelectedTicket(null);
  };

  const handleApproveTicket = (ticket) => {
    if (String(ticket?.status || '').toUpperCase() !== TICKET_STATUSES.PENDING) {
      error('Only pending tickets can be approved');
      return;
    }

    setApproveTicket(ticket);
    setShowApproveModal(true);
  };

  const handleConfirmApproval = async (ticketId, payload) => {
    const result = await ticketService.closeTicket(ticketId, payload);

    if (result.success) {
      success(`Ticket #${ticketId} approved successfully`);
      setShowApproveModal(false);
      setApproveTicket(null);
      await loadData();
    } else {
      error(result.error || 'Failed to approve ticket');
    }
  };

  const handleReopenTicket = (ticket) => {
    if (String(ticket?.status || '').toUpperCase() !== TICKET_STATUSES.PENDING) {
      error('Only pending tickets can be reopened');
      return;
    }

    setReopenTicket(ticket);
    setShowReopenModal(true);
  };

  const handleConfirmReopen = async (ticketId, payload) => {
    const result = await ticketService.reopenTicket(ticketId, payload);

    if (result.success) {
      success(`Ticket #${ticketId} reopened and sent to dispatch.`);
      setShowReopenModal(false);
      setReopenTicket(null);
      await loadData();
    } else {
      error(result.error || 'Failed to reopen ticket');
    }
  };

  const handleExportCSV = async () => {
    const result = await customerService.exportCustomerTicketsCSV(user.id, advancedFilters);
    if (result.success) {
      success('Tickets exported successfully!');
    } else {
      error(result.error || 'Failed to export tickets');
    }
  };

  const handleRefresh = () => {
    loadData();
    success('Tickets refreshed!');
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleClearFilters = () => {
    setAdvancedFilters({});
    setSearchTerm('');
  };

  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  };

  const actionsBarStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
    alignItems: 'center'
  };

  const searchStyle = {
    flex: '1',
    minWidth: '250px'
  };

  const activeFiltersStyle = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '16px'
  };

  const filterBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    background: 'var(--primary-light)',
    color: 'var(--primary)',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500'
  };

  const removeBadgeStyle = {
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '16px'
  };

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => (
        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>
          #{value}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>{value}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {row.system_type}
          </div>
        </div>
      )
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${PRIORITY_COLORS[value] || '#6b7280'}20`,
          color: PRIORITY_COLORS[value] || '#6b7280'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: `${STATUS_COLORS[value] || '#6b7280'}20`,
          color: STATUS_COLORS[value] || '#6b7280'
        }}>
          {value.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'site_name',
      label: 'Site'
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => formatDateTime(value)
    },
    {
      key: 'site_name',
      label: 'Site',
      condition: () => selectedSite === 0
    }
  ];

  const siteOptions = sites?.map(site => ({
    value: site.id.toString(),
    label: site.site_name
  })) || [];

  if (loading || selectedSite === null) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading tickets...
      </div>
    );
  }

  return (
    <div>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {stats && (
        <div style={statsGridStyle}>
          <StatCard title="Total Tickets" value={stats.total} color="#3b82f6" onClick={() => handleTicketCardClick('ALL')} />
          <StatCard title="Open Tickets" value={stats.open} color="#f59e0b" onClick={() => handleTicketCardClick('OPEN')} />
          <StatCard title="Pending Approval" value={stats.pending} color="#10b981" onClick={() => handleTicketCardClick('PENDING')} />
          <StatCard title="Critical" value={stats.critical} color="#ef4444" onClick={() => handleTicketCardClick('CRITICAL')} />
        </div>
      )}

      {!canCreateTicket && contractWarning && (
        <div style={{
          padding: '12px 16px',
          background: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          color: '#b91c1c',
          marginBottom: '20px',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {contractWarning}
        </div>
      )}

      <div style={actionsBarStyle}>
        <div style={searchStyle}>
          <Input
            placeholder="Search by ID, title, or system..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <AdvancedFilters
          onApply={handleApplyFilters}
          onReset={handleClearFilters}
          engineers={[]} 
          sites={siteOptions}
        />
        <Button variant="secondary" onClick={handleRefresh}>
          Refresh
        </Button>
        <Button variant="secondary" onClick={handleExportCSV}>
          Export CSV
        </Button>
        <Button 
          variant="primary" 
          onClick={() => setShowAddModal(true)}
          disabled={!canCreateTicket || selectedSite === 0}
          title={selectedSite === 0 ? "Select a specific site to create a ticket" : !canCreateTicket ? "No active contract for this site" : ""}
        >
          New Ticket
        </Button>
      </div>

      {Object.keys(advancedFilters).length > 0 && (
        <div style={activeFiltersStyle}>
          {advancedFilters.status && (
            <span style={filterBadgeStyle}>
              Status: {advancedFilters.status}
              <span style={removeBadgeStyle} onClick={() => setAdvancedFilters(prev => ({ ...prev, status: '' }))}>×</span>
            </span>
          )}
          {advancedFilters.priority && (
            <span style={filterBadgeStyle}>
              Priority: {advancedFilters.priority}
              <span style={removeBadgeStyle} onClick={() => setAdvancedFilters(prev => ({ ...prev, priority: '' }))}>×</span>
            </span>
          )}
          {advancedFilters.site && (
            <span style={filterBadgeStyle}>
              Site: {sites.find(s => s.id.toString() === advancedFilters.site)?.site_name}
              <span style={removeBadgeStyle} onClick={() => setAdvancedFilters(prev => ({ ...prev, site: '' }))}>×</span>
            </span>
          )}
          <Button variant="secondary" onClick={handleClearFilters}>
            Clear All
          </Button>
        </div>
      )}

      <DataTable
        data={filteredTickets}
        columns={columns.filter(col => typeof col.condition === 'function' ? col.condition() : true)}
        onRowClick={handleViewTicket}
        actions={[
          { label: ' View', onClick: handleViewTicket },
          {
            label: ' Approve',
            onClick: handleApproveTicket,
            condition: (ticket) => String(ticket.status || '').toUpperCase() === TICKET_STATUSES.PENDING
          },
          {
            label: ' Reopen',
            onClick: handleReopenTicket,
            condition: (ticket) => String(ticket.status || '').toUpperCase() === TICKET_STATUSES.PENDING
          }
        ]}
        pageSize={15}
      />

      {showViewModal && selectedTicket && (
        <ViewTicketModal
          isOpen={showViewModal}
          onClose={() => { setShowViewModal(false); setSelectedTicket(null); }}
          ticket={selectedTicket}
          onEdit={handleEditTicket}
        />
      )}

      {showAddModal && (
        <AddTicketModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddTicket}
          sites={siteOptions}
        />
      )}

      {/* Rest of the Modals (Edit, Delete, Approve, Reopen) remain identical */}
      {showEditModal && selectedTicket && (
        <EditTicketModal
          isOpen={showEditModal}
          onClose={() => { setShowEditModal(false); setSelectedTicket(null); }}
          onSubmit={handleUpdateTicket}
          ticket={selectedTicket}
        />
      )}

      {showApproveModal && approveTicket && (
        <ApproveTicketModal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setApproveTicket(null);
          }}
          onSubmit={handleConfirmApproval}
          ticket={approveTicket}
        />
      )}

      {showReopenModal && reopenTicket && (
        <ReopenTicketModal
          isOpen={showReopenModal}
          onClose={() => {
            setShowReopenModal(false);
            setReopenTicket(null);
          }}
          onSubmit={handleConfirmReopen}
          ticket={reopenTicket}
        />
      )}
    </div>
  );
};

export default TicketsView;